import { Alert, Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import useLocalization from '../../hooks/useLocalization';
import KycVerificationPanel from '../molecules/KycVerificationPanel';
import PaymentProviders from '../atoms/PaymentProviders';
import BalanceContact from '../atoms/BalanceContact';
import CloseIcon from '@mui/icons-material/Close';
import { PAYMENT_GATEWAY, PAYMENT_TRANSACTION_TYPE, type PaymentTransactionType } from '../../types/enums';
import { usePaymentFlowState } from '../../hooks/payment/usePaymentFlowState';
import { useWithdrawalBonusGuard } from '../../hooks/payment/useWithdrawalBonusGuard';
import { isActiveWelcomeBonus } from '../../queries/bonus';
import useKycAttempts from '../../hooks/useKycAttempts';
import { useStartKycVerification, useUserData } from '../../queries';
import { useMarketplaceConfig } from '../../queries/marketplace';
import ForfeitBonusDialog from '../molecules/ForfeitBonusDialog';
import PaymentFormRenderer from './PaymentFormRenderer';
import { FormattedBalance } from '../atoms/FormattedBalance';
import { shouldShowTransactionKyc } from '../../utils/kyc';
import WelcomeBonusSelectionGate from '../molecules/WelcomeBonusSelectionGate';
import BonusMinimumDepositNotice from '../molecules/BonusMinimumDepositNotice';
import SwipeableModalShell from '../atoms/SwipeableModalShell';

const styles = {
  rootContainer: {
    p: 3,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'neutral.25',
    pt: { xs: 1.5, md: 0 },
    pb: 1,
  },
  headerButtonContainer: {
    flex: 1,
  },
  headerButtonContainerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  icon: {
    color: 'neutral.25',
    fontSize: { xs: '1rem', md: '1.5rem' },
  },
  closeButton: {
    display: { xs: 'none', md: 'flex' },
  },
  contentStack: {
    mt: { xs: 2, md: 3 },
    gap: 2,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollArea: {
    gap: 2,
  },
  toggleButton: {
    width: '50%',
    height: 40,
    backgroundColor: 'neutral.600',
    color: 'neutral.25',
    borderRadius: 2,
    '&.Mui-selected': {
      color: 'primary.contrastText',
      backgroundColor: 'primary.main',
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
};

const BalanceModal = ({
  handleClose,
  showBalanceContact,
}: {
  handleClose: () => void;
  showBalanceContact: boolean;
}) => {
  const { t } = useLocalization();
  const { data: userData } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const {
    transactionType,
    selectedMethod,
    paymentGateway,
    paymentProviders,
    isDeposit,
    isWithdrawal,
    cryptoCashier,
    showPaymentProviderPicker,
    showPaymentContent,
    withdrawalDisabled,
    withdrawalDisabledReason,
    handleTransactionTypeChange,
    handleProviderChange,
    resetToDeposit,
  } = usePaymentFlowState();

  const {
    firstDepositBonus,
    isForfeitDialogOpen,
    shouldBlockWithdrawal,
    setWithdrawalBonusCleared,
    closeForfeitDialog,
    handleForfeitCancel,
  } = useWithdrawalBonusGuard({ isWithdrawal, resetToDeposit });
  const { mutate: startVerification, isPending: isVerificationPending } = useStartKycVerification();
  const { hasReachedLimit } = useKycAttempts(userData?.kycAttempts);
  const marketplaceKycConfig = marketplaceConfig?.marketplaceConfig.kyc;
  const showForm = showPaymentContent && !shouldBlockWithdrawal;
  const shouldShowBalanceContact = showBalanceContact || paymentGateway === PAYMENT_GATEWAY.FINPSP;
  const shouldShowKycGate = shouldShowTransactionKyc(userData, marketplaceKycConfig, transactionType);

  const handleTransactionTypeChangeWrapper = (
    _event: MouseEvent<HTMLElement>,
    newTransactionType: PaymentTransactionType | null
  ) => {
    handleTransactionTypeChange(newTransactionType);
  };

  return (
    <SwipeableModalShell sx={styles.rootContainer} testId='balance-modal-shell'>
      <Box sx={styles.header}>
        <Box sx={styles.headerButtonContainer} />
        <Typography
          variant='h3'
          sx={{
            textAlign: 'center',
            color: 'neutral.200',
          }}
        >
          {t('balance')}: <FormattedBalance balanceType='main' />
        </Typography>
        <Box sx={styles.headerButtonContainerRight}>
          <IconButton aria-label={t('close')} onClick={handleClose} sx={styles.closeButton}>
            <CloseIcon sx={styles.icon} />
          </IconButton>
        </Box>
      </Box>
      <Stack sx={styles.contentStack}>
        <ToggleButtonGroup
          color='primary'
          value={transactionType}
          exclusive
          onChange={handleTransactionTypeChangeWrapper}
          aria-label='Platform'
        >
          <ToggleButton sx={styles.toggleButton} value={PAYMENT_TRANSACTION_TYPE.DEPOSIT}>
            {t('deposit')}
          </ToggleButton>
          <ToggleButton
            sx={styles.toggleButton}
            value={PAYMENT_TRANSACTION_TYPE.WITHDRAW}
            disabled={withdrawalDisabled}
          >
            {t('withdraw')}
          </ToggleButton>
        </ToggleButtonGroup>
        {withdrawalDisabledReason === 'provider_unavailable' && (
          <Alert severity='info'>{t('zeroXWithdrawalUnavailable')}</Alert>
        )}
        <SwipeableModalShell.ScrollArea sx={styles.scrollArea} testId='balance-modal-scroll-area'>
          <WelcomeBonusSelectionGate enabled={isDeposit}>
            {shouldShowKycGate ? (
              <KycVerificationPanel
                attemptsExceeded={hasReachedLimit}
                isPending={isVerificationPending}
                onStartVerification={() => startVerification()}
                status={userData?.kycStatus}
                trigger={marketplaceKycConfig?.trigger}
              />
            ) : (
              <>
                {isDeposit && <BonusMinimumDepositNotice bonus={firstDepositBonus} />}
                {!shouldBlockWithdrawal && showPaymentProviderPicker && (
                  <PaymentProviders
                    transactionType={transactionType}
                    selectedProvider={selectedMethod}
                    onProviderChange={handleProviderChange}
                    paymentProviders={paymentProviders}
                    paymentGateway={paymentGateway}
                  />
                )}
                {!shouldBlockWithdrawal && shouldShowBalanceContact && <BalanceContact />}
                {showForm && (
                  <PaymentFormRenderer
                    cryptoCashier={cryptoCashier}
                    paymentGateway={paymentGateway}
                    transactionType={transactionType}
                    paymentProvider={selectedMethod}
                  />
                )}
              </>
            )}
          </WelcomeBonusSelectionGate>
        </SwipeableModalShell.ScrollArea>
      </Stack>
      {firstDepositBonus && isActiveWelcomeBonus(firstDepositBonus) && (
        <ForfeitBonusDialog
          bonus={firstDepositBonus}
          onCancel={handleForfeitCancel}
          onClose={closeForfeitDialog}
          onSuccess={() => {
            setWithdrawalBonusCleared(true);
            closeForfeitDialog();
          }}
          open={isForfeitDialogOpen}
          productType={firstDepositBonus.campaignProduct}
        />
      )}
    </SwipeableModalShell>
  );
};

export default BalanceModal;
